/**
 * DraftBoard — landing/picker page (Draft Board rebuild, Stage 0)
 *
 * Draft Board itself is one page: three full-width dial-icon tiles, one per
 * section (Strategy Establishment / Live Draft / Draft Review), stacked
 * vertically. Clicking a tile navigates AWAY to that section's own full
 * page (pages/draft/*.jsx) — this page doesn't stay mounted underneath it.
 * Each section page links back here via ToolHeader's `backRight` prop ("Go to
 * Draft Board"). No feature logic on this page — that's Stages 1-3. See
 * engineering/ARCHIVE/completed-prompts/JAI_DraftBoard_Rebuild_Prompt.md.
 *
 * The previous single-file implementation (pre-draft strategy chat + live
 * draft polling, ~2000 lines, DraftBoard.legacy.jsx) was deleted in Stage 4
 * once Stage 3 verified clean — its getSnakePicks is fully superseded by
 * draftMathService.computePickList (which actually handles 3rd-round
 * reversal, which getSnakePicks never did).
 */

import { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useLeagues } from '../context/LeagueContext'
import { draftBoardApi } from '../services/api'
import { useMinWidth } from '../hooks/useMinWidth'
import { trackEvent } from '../analytics'
import AppShell from '../components/AppShell'
import ToolHeader from '../components/ToolHeader'
import { JAI_FONTS } from '../components/JaiBrand'
import { V2, V2Dial, V2ToolGlyphs } from '../components/JaiV2'

const SECTIONS = [
  { key: 'strategy', glyph: 'strategy', label: 'Strategy Establishment', sub: 'Set your draft plan before you\'re on the clock', path: 'strategy' },
  { key: 'live',      glyph: 'live',     label: 'Live Draft',             sub: 'Real-time recommendations as picks come in',    path: 'live' },
  { key: 'review',    glyph: 'review',   label: 'Draft Review',           sub: 'A graded recap of every pick you made',         path: 'review' },
]

/**
 * Where this league is in the three-step journey, in plain words, so each tile
 * can say its status and the next one to do stands out. `ctx` is the cheap,
 * no-AI GET /draft/context answer (null until it loads or if it fails, in which
 * case the tiles simply show no status).
 */
const journeyStatus = (ctx, draftDone) => {
  if (!ctx) return { next: null, tiles: {} }
  const locked = !!ctx.lockedStrategy
  const confirmed = !!ctx.context?.confirmedByUser
  const strategy = locked
    ? { text: `Locked: ${ctx.lockedStrategy.name}`, tone: 'done' }
    : confirmed ? { text: 'Pick a strategy', tone: 'todo' } : { text: 'Start here', tone: 'todo' }
  const live = draftDone
    ? { text: 'Draft finished', tone: 'done' }
    : locked ? { text: 'Ready when your draft starts', tone: 'todo' } : { text: 'Set a strategy first', tone: 'wait' }
  const review = draftDone
    ? { text: 'Ready to review', tone: 'todo' }
    : { text: 'After your draft', tone: 'wait' }
  const next = !locked && !draftDone ? 'strategy' : draftDone ? 'review' : 'live'
  return { next, tiles: { strategy, live, review } }
}

const STATUS_COLOR = { done: V2.success, todo: V2.blue, wait: V2.faint }

export default function DraftBoard() {
  const { id } = useParams()
  const { leagues } = useLeagues()
  const league = leagues.find(l => l.id === Number(id))
  const wide = useMinWidth(820)
  const [ctx, setCtx] = useState(null)

  useEffect(() => {
    let cancelled = false
    draftBoardApi.getContext(id)
      .then((res) => { if (!cancelled) setCtx(res.data) })
      .catch(() => { /* status is a nicety; the tiles work without it */ })
    trackEvent('draft_room_opened')
    return () => { cancelled = true }
  }, [id])

  const { next, tiles } = journeyStatus(ctx, !!league?.draftCompleted)

  return (
    <AppShell>
      <div style={{ padding: wide ? '30px 40px' : '20px 16px', fontFamily: JAI_FONTS.tight }}>
        <div style={{ maxWidth: 880, margin: '0 auto' }}>
          <ToolHeader
            leagueName={league?.name}
            title="Draft Room"
            back={{ label: 'Go to League Home Screen', to: `/league/${id}` }}
          />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {SECTIONS.map(section => (
              <Link
                key={section.key}
                to={`/league/${id}/draft/${section.path}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 20,
                  width: '100%', textDecoration: 'none',
                  background: 'white', border: `1px solid ${next === section.key ? 'rgba(30,91,214,0.45)' : V2.hairSoft}`, borderRadius: 16,
                  padding: wide ? '26px 28px' : '18px 16px', transition: 'border-color 180ms, box-shadow 180ms',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(30,91,214,0.3)'; e.currentTarget.style.boxShadow = V2.cardShadow }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = next === section.key ? 'rgba(30,91,214,0.45)' : V2.hairSoft; e.currentTarget.style.boxShadow = 'none' }}
              >
                <V2Dial size={56}>{V2ToolGlyphs[section.glyph]}</V2Dial>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 17, fontWeight: 800, color: V2.navy, letterSpacing: '0.02em', textTransform: 'uppercase', marginBottom: 3 }}>{section.label}</div>
                  <div style={{ fontSize: 13, color: V2.mute }}>{section.sub}</div>
                  {tiles[section.key] && (
                    <div data-testid={`draft-tile-status-${section.key}`} style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', marginTop: 7, color: STATUS_COLOR[tiles[section.key].tone] }}>
                      {next === section.key ? 'Next: ' : ''}{tiles[section.key].text}
                    </div>
                  )}
                </div>
                <span style={{ fontSize: 20, color: V2.faint, flexShrink: 0 }}>&rarr;</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </AppShell>
  )
}
